import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { get } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";

export interface AccountSummary {
  account_id: string;
  account_name: string;
  city: string | null;
  country: string | null;
  region: string | null;
  industry: string | null;
  assessment_level: string | number | null;
  renewal_date: string | null;
  revenue: number | null;
  tier: string | null;
}

export interface AssessmentTrendlinePoint {
  published_date: string;
  published_at?: string;
  level: string;
  level_number: number;
  assessment_id?: string;
  assessment_name?: string;
  source?: string;
}

export interface AssessmentTrendline {
  account_id: string;
  account_name: string;
  from_date: string;
  to_date: string;
  points: AssessmentTrendlinePoint[];
  incomplete_assessments?: unknown[];
  total_points: number;
}

export type FetchAssessmentTrendlineParams = {
  accountId: string;
  from_date: string;
  to_date: string;
};

interface AccountWorkspaceState {
  accountSummary: AccountSummary | null;
  loading: boolean;
  error: string | null;
  assessmentTrendline: AssessmentTrendline | null;
  assessmentTrendlineLoading: boolean;
  assessmentTrendlineError: string | null;
}

const initialState: AccountWorkspaceState = {
  accountSummary: null,
  loading: false,
  error: null,
  assessmentTrendline: null,
  assessmentTrendlineLoading: false,
  assessmentTrendlineError: null,
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  const err = error as {
    response?: { data?: { detail?: string; message?: string } };
    message?: string;
  };
  return (
    err?.response?.data?.detail ||
    err?.response?.data?.message ||
    err?.message ||
    fallback
  );
};

export const fetchAccountSummary = createAsyncThunk(
  "accountWorkspace/fetchAccountSummary",
  async (accountId: string, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.getAccountSummary(accountId));
      return response as AccountSummary;
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to fetch account details")
      );
    }
  },
  {
    condition: (accountId, { getState }) => {
      const { accountWorkspace } = getState() as {
        accountWorkspace: AccountWorkspaceState;
      };
      if (accountWorkspace.loading) return false;
      if (accountWorkspace.accountSummary?.account_id === accountId) {
        return false;
      }
      return true;
    },
  }
);

export const fetchAssessmentTrendline = createAsyncThunk(
  "accountWorkspace/fetchAssessmentTrendline",
  async (params: FetchAssessmentTrendlineParams, { rejectWithValue }) => {
    try {
      const response = await get(
        API_URLS.getAssessmentTrendline(params.accountId, {
          from_date: params.from_date,
          to_date: params.to_date,
        })
      );
      return response as AssessmentTrendline;
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to load assessment trend")
      );
    }
  }
);

const accountWorkspaceSlice = createSlice({
  name: "accountWorkspace",
  initialState,
  reducers: {
    clearAccountSummary: state => {
      state.accountSummary = null;
      state.error = null;
      state.loading = false;
      state.assessmentTrendline = null;
      state.assessmentTrendlineLoading = false;
      state.assessmentTrendlineError = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAccountSummary.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAccountSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.accountSummary = action.payload;
        state.error = null;
      })
      .addCase(fetchAccountSummary.rejected, (state, action) => {
        state.loading = false;
        state.accountSummary = null;
        state.error =
          (action.payload as string) ||
          action.error.message ||
          "Failed to fetch account details";
      })
      .addCase(fetchAssessmentTrendline.pending, state => {
        state.assessmentTrendlineLoading = true;
        state.assessmentTrendlineError = null;
      })
      .addCase(fetchAssessmentTrendline.fulfilled, (state, action) => {
        state.assessmentTrendlineLoading = false;
        state.assessmentTrendline = action.payload;
        state.assessmentTrendlineError = null;
      })
      .addCase(fetchAssessmentTrendline.rejected, (state, action) => {
        state.assessmentTrendlineLoading = false;
        state.assessmentTrendline = null;
        state.assessmentTrendlineError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to load assessment trend";
      });
  },
});

export const { clearAccountSummary } = accountWorkspaceSlice.actions;
export default accountWorkspaceSlice.reducer;
